import React from 'react';
import { Grid, Paper, Box, Typography, Fab, Button } from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import ChatWindow from '../ChatWindow';
import ReactMarkdown from 'react-markdown';
import ClarityAnalytics from '../clarity';

const ProjectDescriptionTab = ({
  projectMessages,
  handleProjectMessage,
  handleEditMessage,
  handleDeleteMessage,
  isLoading,
  requirementSummary,
  selectedProject,
  projectMarkdown,
  handleNextStepFromDescription,
  gridContainerStyles,
  leftColumnStyles,
  rightColumnStyles
}) => {
  const handleDownload = () => {
    const markdownContent = projectMarkdown[projectMarkdown.length - 1];
    const blob = new Blob([markdownContent], { type: "text/markdown" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "project_plan.md";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <>
    <><ClarityAnalytics /></>
    <Grid container {...gridContainerStyles}>
      <Grid item {...leftColumnStyles}>
        <Paper elevation={0} className="bg-transparent">
          <ChatWindow
            messages={projectMessages}
            onSendMessage={handleProjectMessage}
            placeholder="Enter project description..."
            title="Project Chat"
            onEditMessage={(index) => handleEditMessage("project", index)}
            onDeleteMessage={(ind) => handleDeleteMessage("project", ind)}
            isLoading={isLoading}
            summaries={requirementSummary}
            selectedProject={selectedProject}
            type="project"
          />
          {projectMessages.length >= 1 && (
            <Box className="mt-auto p-4">
              <Button
                variant="contained"
                onClick={handleNextStepFromDescription}
                disabled={isLoading}
                sx={{
                  backgroundColor: "#1a1a1a",
                  "&:hover": {
                    backgroundColor: "#333333",
                  },
                  "&:disabled": {
                    backgroundColor: "#666666",
                  },
                }}
              >
                Next Step: Generate Requirements
              </Button>
            </Box>
          )}
        </Paper>
      </Grid>

      <Grid item {...rightColumnStyles}>
        <ProjectPlanView 
          projectMarkdown={projectMarkdown}
          onDownload={handleDownload}
        />
      </Grid>
    </Grid>
    </>
  );
};

// Separate component for the right panel
const ProjectPlanView = ({ projectMarkdown, onDownload }) => (
  <Paper elevation={1} className="p-4 bg-white rounded-lg">
    <Box className="flex justify-between items-center mb-4">
      <Typography variant="h6" className="font-semibold">
        Project Plan
      </Typography>
    </Box>
    <Box className="flex-grow overflow-y-auto">
      {projectMarkdown
        ?.slice()
        .reverse()
        .map((md, index, array) => (
          <React.Fragment key={index}>
            <VersionedMarkdown 
              content={md} 
              versionNumber={array.length - index} 
              isLastItem={index === array.length - 1}
            />
          </React.Fragment>
        ))}
    </Box>

    <DownloadFab 
      onDownload={onDownload} 
      disabled={!projectMarkdown.length} 
    />
  </Paper>
);
const MarkdownRenderer = ({ markdownText }) => {
  return (
    <div className="markdown-container">
      <ReactMarkdown>{markdownText}</ReactMarkdown>
    </div>
  );
};
// Component for each versioned markdown entry
const VersionedMarkdown = ({ content, versionNumber, isLastItem }) => (
  <>
    <Paper className="p-4 mb-3 bg-gray-50 rounded-lg" elevation={0}>
      <Typography
        variant="caption"
        className="block mb-2 text-gray-500"
        sx={{ fontWeight: 500 }}
      >
        Version {versionNumber}
      </Typography>
      <Typography className="text-gray-800" style={{ whiteSpace: "pre-wrap" }}>
        <MarkdownRenderer markdownText={content} />
      </Typography>
    </Paper>

    {!isLastItem && <VersionDivider />}
  </>
);

// Component for the version divider
const VersionDivider = () => (
  <Box className="flex items-center justify-center mb-3" sx={{ color: "text.secondary" }}>
    <Box className="flex-grow border-t border-gray-300" />
    <Typography variant="caption" className="mx-2 text-gray-500">
      Previous Version
    </Typography>
    <Box className="flex-grow border-t border-gray-300" />
  </Box>
);

// Component for the download FAB
const DownloadFab = ({ onDownload, disabled }) => (
  <Fab
    color="primary"
    aria-label="download"
    onClick={onDownload}
    disabled={disabled}
    sx={{
      position: { xs: "fixed", md: "absolute" },
      bottom: { xs: 16, md: 32 },
      right: { xs: 16, md: 32 },
      backgroundColor: "#1a1a1a",
      "&:hover": {
        backgroundColor: "#333333",
      },
      "&:disabled": {
        backgroundColor: "#666666",
      },
    }}
  >
    <DownloadIcon />
  </Fab>
);

export default ProjectDescriptionTab; 